import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {AuthCheck} from '../../helpers'
import {
  SearchUserTaskClient,
  GetUserTaskClient,
  DeleteUserTaskClient,
  UpdateUserTaskClient,
  CreateUserTaskClient,
  CompleteUserTaskClient,
} from '../../api'

const SERVICE_URL = window.ENVVARS.REACT_APP_SERVICE_URL

// API Clients
const searchUserTaskClient = new SearchUserTaskClient(SERVICE_URL)
const getUserTaskClient = new GetUserTaskClient(SERVICE_URL)
const createUserTaskClient = new CreateUserTaskClient(SERVICE_URL)
const deleteUserTaskClient = new DeleteUserTaskClient(SERVICE_URL)
const updateUserTaskClient = new UpdateUserTaskClient(SERVICE_URL)
const completeUserTaskClient = new CompleteUserTaskClient(SERVICE_URL)

const initialState = {
  loading: false, 
  isRedirect: false,
  error: null,
  isDeleted: false,
  detail: null,
  completeTask: false,
  userTask: null,
}

// Async Thunks
export const userTaskGetAll = createAsyncThunk('userTask/getAll', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await searchUserTaskClient.search(
      params.name,
      params.userTaskStatusId,
      params.taskPriorityId,
      params.startDate,
      params.endDate,
      params.items_per_page,
      params.page,
      params.order,
      params.sort
    )
    dispatch(setUserTask(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const userTaskGetId = createAsyncThunk('userTask/getId', async (id, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await getUserTaskClient.get(id)
    dispatch(setDetail(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const userTaskDelete = createAsyncThunk('userTask/delete', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await deleteUserTaskClient.delete(params)
    dispatch(setDeleted(true))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const userTaskCreate = createAsyncThunk('userTask/create', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await createUserTaskClient.create(params)
    dispatch(setRedirect(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const userTaskUpdate = createAsyncThunk('userTask/update', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await updateUserTaskClient.update(params)
    dispatch(setRedirect(response || params))
    dispatch(setDetail(params))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setDetail(params))
    dispatch(setError(error))
    throw error
  }
})

export const userTaskComplete = createAsyncThunk(
  'userTask/complete',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await completeUserTaskClient.update(params)
      dispatch(setCompleteTask(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

// Slice
const userTaskSlice = createSlice({
  name: 'userTask',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
      state.loading = false
    },
    setUserTask: (state, action) => {
      state.userTask = action.payload
      state.loading = false
    },
    setDetail: (state, action) => {
      state.detail = action.payload
      state.loading = false
    },
    setDeleted: (state, action) => {
      state.isDeleted = action.payload
      state.loading = false
    },
    setRedirect: (state, action) => {
      state.isRedirect = action.payload
      state.loading = false
    },
    setCompleteTask: (state, action) => {
      state.completeTask = action.payload
      state.loading = false
    },
    resetState: (state) => {
      Object.assign(state, initialState)
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type.startsWith('userTask/') && action.type.endsWith('/pending'),
      (state) => {
        state.loading = true
      }
    )
  }
})

// Export actions
export const {
  setLoading,
  setError,
  setUserTask,
  setDetail,
  setDeleted,
  setRedirect,
  setCompleteTask,
  resetState,
} = userTaskSlice.actions

// Selectors
export const selectUserTaskLoading = (state) => state.userTask.loading
export const selectUserTaskError = (state) => state.userTask.error
export const selectUserTask = (state) => state.userTask.userTask
export const selectUserTaskDetail = (state) => state.userTask.detail
export const selectUserTaskDeleted = (state) => state.userTask.isDeleted
export const selectUserTaskRedirect = (state) => state.userTask.isRedirect
export const selectUserTaskComplete = (state) => state.userTask.completeTask

export default userTaskSlice.reducer
